import { entityName, imageIdOf, SPAN_ROLES, type Entity, type SpanRole, type StoryCtx } from '@dispositio/core';
import { useEffect, useRef, type CSSProperties } from 'react';
import { de } from '../../i18n/de';
import { linkPresetOf, shortcutOf } from '../../lib/linkShortcuts';
import { useStoryEnv } from '../../state/storyEnv';
import type { RefKind } from './RefBrowser';
import { AssetImg } from '../AssetImg';

/** Was ein Klick im Menü eines angehefteten Elements macht: Nennung („Ist …“) oder Bezug („Gehört zu …“ mit Art) */
export type PinChoice = { kind: 'name' } | { kind: 'span'; role: SpanRole | null };

interface Props {
  /** die angehefteten Einträge, in der Reihenfolge des Anheftens */
  entities: Entity[];
  /** offenes Menü: für welches Element, an welcher Stelle (Text), vorgeschlagene Art, Lage des Symbols */
  menu: { entityId: string; text: string; suggest: RefKind; anchor: DOMRect } | null;
  onOpen: (entity: Entity, anchor: DOMRect) => void;
  onPick: (choice: PinChoice) => void;
  onClose: () => void;
  onUnpin: (entityId: string) => void;
}

const MENU_WIDTH = 248;

/** Name, Farbe und Bild eines Eintrags (wie im Referenz-Browser) */
function lookOf(ctx: StoryCtx, e: Entity): { title: string; color: string; imageId: string | null; typeName: string } {
  const type = ctx.types.get(e.typeId);
  return {
    title: entityName(e, type) || e.refKey,
    color: type?.color ?? 'var(--accent)',
    imageId: type ? imageIdOf(type, e.fields, e.colorSlot, e.id) : null,
    typeName: type?.name ?? '',
  };
}

/**
 * Angeheftete Elemente rechts neben dem Text (angeheftet mit der Nadel im Referenz-Browser): je Element sein rundes Bild, untereinander.
 * Ein Klick darauf verknüpft die Markierung (bzw. das Wort am Cursor) ohne den Browser – im Menü daneben wählt man die Art: „Ist …“
 * oder „Gehört zu …“ mit Rede, Handlung, Gedanke bzw. ohne Art. Die Markierung bleibt dabei erhalten (die Knöpfe nehmen dem Text den
 * Fokus nicht).
 */
export function RefPins({ entities, menu, onOpen, onPick, onClose, onUnpin }: Props) {
  const { ctx } = useStoryEnv();
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent): void => {
      if (host.current && !host.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      // Strg+⇧+1 … 5 wählt die Art (statt im Text den Browser zu öffnen)
      const preset = linkPresetOf(e);
      if (e.key !== 'Escape' && !preset) return;
      e.preventDefault();
      e.stopPropagation();
      if (preset) onPick(preset);
      else onClose();
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [menu, onClose, onPick]);

  if (entities.length === 0) return null;
  const open = menu ? entities.find((e) => e.id === menu.entityId) : undefined;
  const look = open ? lookOf(ctx, open) : null;
  const keep = (e: { preventDefault: () => void }): void => e.preventDefault();
  const short = menu ? (menu.text.length > 40 ? `${menu.text.slice(0, 38)}…` : menu.text) : '';

  return (
    <div ref={host} className="ref-pins" role="toolbar" aria-orientation="vertical" aria-label={de.refPins.rail} data-testid="ref-pins">
      {entities.map((e) => {
        const l = lookOf(ctx, e);
        return (
          <div key={e.id} className="rp-item">
            <button
              type="button"
              className={`rp-pin${menu?.entityId === e.id ? ' open' : ''}`}
              style={{ '--c': l.color } as CSSProperties}
              title={de.refPins.open(l.title)}
              aria-label={de.refPins.open(l.title)}
              aria-haspopup="dialog"
              aria-expanded={menu?.entityId === e.id}
              onMouseDown={keep}
              onClick={(ev) => onOpen(e, ev.currentTarget.getBoundingClientRect())}
              data-testid="rp-pin"
              data-ref={e.refKey}
            >
              {l.imageId ? <AssetImg id={l.imageId} alt="" loading="lazy" /> : <span>{l.title.slice(0, 1).toUpperCase()}</span>}
            </button>
            {/* ✕ beim Überfahren: nicht mehr anheften */}
            <button
              type="button"
              className="rp-remove"
              title={de.refPins.unpinHint(l.title)}
              aria-label={de.refPins.unpinHint(l.title)}
              onMouseDown={keep}
              onClick={() => onUnpin(e.id)}
              data-testid="rp-remove"
              data-ref={e.refKey}
            >
              ✕
            </button>
          </div>
        );
      })}
      {menu && open && look && (
        <div
          className="ref-pin-menu"
          role="dialog"
          aria-label={de.refPins.title(short, look.title)}
          style={{ top: Math.max(8, Math.min(menu.anchor.top - 6, window.innerHeight - 260)), left: Math.max(8, menu.anchor.left - MENU_WIDTH - 10), width: MENU_WIDTH }}
          onMouseDown={keep}
          data-testid="ref-pin-menu"
        >
          <div className="rpm-head">
            <strong className="rpm-name">{look.title}</strong>
            <span className="small muted rpm-text">„{short}“</span>
          </div>
          <button
            type="button"
            className={`rpm-opt${menu.suggest === 'name' ? ' suggest' : ''}`}
            title={de.refBrowser.kindNameHint(shortcutOf({ kind: 'name' })!)}
            onClick={() => onPick({ kind: 'name' })}
            data-testid="rpm-name"
          >
            {de.refBrowser.kindName}
          </button>
          <div className={`rpm-span${menu.suggest === 'span' ? ' suggest' : ''}`}>
            <span className="small rpm-label">{de.refPins.spanWith}</span>
            <div className="rpm-roles" role="group" aria-label={de.refBrowser.role}>
              {SPAN_ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  className="rpm-role"
                  title={de.refBrowser.roleHint(`${de.refBrowser.kindSpan} ${de.refBrowser.roles[r]}`, shortcutOf({ kind: 'span', role: r }))}
                  onClick={() => onPick({ kind: 'span', role: r })}
                  data-testid={`rpm-role-${r}`}
                >
                  {de.refBrowser.roles[r]}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="btn small ghost rpm-unpin" title={de.refPins.unpinHint(look.title)} onClick={() => onUnpin(open.id)} data-testid="rpm-unpin">
            {de.refPins.unpin}
          </button>
        </div>
      )}
    </div>
  );
}
